import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Eye, Edit3, Trash2, CheckCircle, Circle, Loader2, Link as LinkIcon, CheckCheck, Search } from 'lucide-react';
import UserSelect from '../components/UserSelect';
import BatchAssignModal from '../components/BatchAssignModal';
import { EavAction } from '../components/EavListToolkit';
import { useTranslation } from '../context/LanguageContext';
import { usePermission } from '../context/PermissionContext';
import UniversalList from '../components/UniversalList';

const TaskList = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const navigate = useNavigate();

  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [departments, setDepartments] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [batchAssignIds, setBatchAssignIds] = useState(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (assigneeFilter) params.append('assignee_id', assigneeFilter);
      if (departmentFilter) params.append('department_id', departmentFilter);

      const res = await fetch(`/api/tasks?${params}`);
      const data = await res.json();
      if (data.status === 'success') {
        setTasks(data.data || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      console.error('Failed to fetch tasks:', e);
    }
    setLoading(false);
  }, [page, limit, search, statusFilter, assigneeFilter, departmentFilter]);

  // Load departments
  useEffect(() => {
    fetch('/api/organization/departments')
      .then(r => r.json())
      .then(data => { if (data.status === 'success') setDepartments(data.data); })
      .catch(console.error);
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, assigneeFilter, departmentFilter]);

  const handleUpdateStatus = async (id, status) => {
    if (!window.confirm(t(status === 'completed' ? 'tasks.confirm_complete' : 'tasks.confirm_processing'))) return;
    try {
      const res = await fetch(`/api/tasks/complete/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (data.status === 'success') fetchTasks();
    } catch (e) {
      console.error('Update status failed:', e);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('tasks.confirm_delete'))) return;
    try {
      const res = await fetch(`/api/tasks/delete/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.status === 'success') { fetchTasks(); setSelectedIds([]); }
    } catch (e) {
      console.error('Delete failed:', e);
    }
  };

  const handleBatchDelete = async (ids) => {
    if (!window.confirm(t('tasks.confirm_batch_delete', '确定删除选中的 ' + ids.length + ' 个任务吗？'))) return;
    try {
      const res = await fetch('/api/tasks/batch-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      const data = await res.json();
      if (data.status === 'success') { fetchTasks(); setSelectedIds([]); }
      else { alert(data.message || 'Batch delete failed'); }
    } catch (e) {
      console.error('Batch delete failed:', e);
      alert('Batch delete failed');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === tasks.length && tasks.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(tasks.map(t => t.id));
    }
  };

  const totalPages = Math.ceil(total / limit);

  const columns = [
    {
      key: '_checkbox',
      label: (
        <input
          type="checkbox"
          checked={selectedIds.length === tasks.length && tasks.length > 0}
          onChange={toggleSelectAll}
          className="rounded border-slate-300 dark:border-slate-600"
          onClick={e => e.stopPropagation()}
        />
      ),
      headerClassName: 'w-10 text-center',
      className: 'w-10 text-center',
      render: (_, row) => (
        <input
          type="checkbox"
          checked={selectedIds.includes(row.id)}
          onChange={() => toggleSelect(row.id)}
          className="rounded border-slate-300 dark:border-slate-600"
          onClick={e => e.stopPropagation()}
        />
      ),
    },
    {
      key: 'id',
      label: t('tasks.id'),
      className: 'text-center font-mono text-xs text-slate-500 dark:text-slate-400',
      render: (val) => `#${val}`,
    },
    {
      key: 'status',
      label: t('tasks.status'),
      className: 'text-center',
      render: (val) => (
        val === 'completed' ? (
          <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
            <CheckCircle size={16} />
            <span className="hidden sm:inline">{t('tasks.completed')}</span>
          </span>
        ) : val === 'processing' ? (
          <span className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400">
            <Loader2 size={16} className="animate-spin" />
            <span className="hidden sm:inline">{t('tasks.processing')}</span>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
            <Circle size={16} />
            <span className="hidden sm:inline">{t('tasks.pending')}</span>
          </span>
        )
      ),
    },
    {
      key: 'name',
      label: t('tasks.name'),
      className: 'text-center',
      render: (val, row) => (
        <div>
          <div className="font-medium text-slate-800 dark:text-slate-200">{val}</div>
          {row.reference_link && (
            <a href={row.reference_link} target="_blank" rel="noopener noreferrer"
              className="text-xs text-blue-500 hover:text-blue-600 inline-flex items-center gap-0.5 mt-0.5">
              <LinkIcon size={10} />
              {row.reference_link.length > 40 ? row.reference_link.slice(0, 40) + '...' : row.reference_link}
            </a>
          )}
        </div>
      ),
    },
    {
      key: 'assignee_name',
      label: t('tasks.assignee'),
      className: 'text-center hidden md:table-cell text-slate-600 dark:text-slate-300',
      render: (val, row) => val ? `${row.assignee_id} - ${val.replace(/\s/g, '')}` : '-',
    },
    {
      key: 'department_name',
      label: t('tasks.department'),
      className: 'text-center hidden md:table-cell text-slate-600 dark:text-slate-300',
      render: (val) => val || '-',
    },
    {
      key: 'creator_name',
      label: t('tasks.creator'),
      className: 'text-center hidden md:table-cell text-slate-500 dark:text-slate-400',
      render: (val, row) => val ? `${row.created_by} - ${val.replace(/\s/g, '')}` : '-',
    },
    {
      key: 'created_at',
      label: t('tasks.created_at'),
      className: 'text-center hidden lg:table-cell text-slate-500 dark:text-slate-400 text-xs',
      render: (val) => val ? val.substring(0, 19).replace('T', ' ') : '-',
    },
    {
      key: '_actions',
      label: t('tasks.actions'),
      headerClassName: 'text-center',
      className: 'text-center',
      render: (_, row) => (
        <div className="flex items-center justify-center gap-1" onClick={e => e.stopPropagation()}>
          {hasPermission('task', 'complete') && row.status === 'pending' && (
            <button onClick={() => handleUpdateStatus(row.id, 'processing')}
              className="p-1.5 rounded-lg transition-colors text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              title={t('tasks.mark_processing')}>
              <Loader2 size={16} />
            </button>
          )}
          {hasPermission('task', 'complete') && row.status !== 'completed' && (
            <button onClick={() => handleUpdateStatus(row.id, 'completed')}
              className="p-1.5 rounded-lg transition-colors text-slate-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
              title={t('tasks.mark_complete')}>
              <CheckCheck size={16} />
            </button>
          )}
          <button onClick={() => navigate(`/tasks/view/${row.id}`)}
            className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
            title={t('tasks.view')}>
            <Eye size={16} />
          </button>
          {hasPermission('task', 'edit') && (
            <button onClick={() => navigate(`/tasks/edit/${row.id}`)}
              className="p-1.5 text-slate-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors"
              title={t('tasks.edit')}>
              <Edit3 size={16} />
            </button>
          )}
          {hasPermission('task', 'delete') && (
            <button onClick={() => handleDelete(row.id)}
              className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              title={t('tasks.delete')}>
              <Trash2 size={16} />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Title + Toolbar */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
          {t('tasks.title')}
        </h1>
      </div>

      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
        <div className="md:hidden" />
        <div className="flex flex-wrap gap-3 md:justify-end">
          {hasPermission('task', 'add') && (
            <button
              onClick={() => navigate('/tasks/new')}
              className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus size={18} />
              {t('tasks.create')}
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="relative">
        <div className="flex flex-col md:flex-row flex-wrap gap-3">
          {/* Search */}
          <div className="relative w-full md:flex-1 md:min-w-[200px] md:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" size={18} />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('tasks.search_placeholder')}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white text-sm"
            />
          </div>
          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="w-full md:w-auto px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
          >
            <option value="">{t('tasks.all_status')}</option>
            <option value="pending">{t('tasks.pending')}</option>
            <option value="processing">{t('tasks.processing')}</option>
            <option value="completed">{t('tasks.completed')}</option>
          </select>
          {/* Department filter */}
          <select
            value={departmentFilter}
            onChange={e => setDepartmentFilter(e.target.value)}
            className="w-full md:w-auto px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
          >
            <option value="">{t('tasks.filter_by_department')}</option>
            {departments.map(dept => (
              <option key={dept.id} value={dept.id}>{dept.name}</option>
            ))}
          </select>
          {/* Assignee filter */}
          <div className="w-full md:w-48">
            <UserSelect
              value={assigneeFilter}
              onChange={val => setAssigneeFilter(val)}
              placeholder={t('tasks.filter_by_assignee')}
            />
          </div>
          {(hasPermission('task', 'assign') || hasPermission('task', 'delete')) && (
            <EavAction
              selectedIds={selectedIds}
              onBatchDelete={hasPermission('task', 'delete') ? handleBatchDelete : undefined}
              onBatchAssignResponsible={hasPermission('task', 'assign') ? (ids) => setBatchAssignIds(ids) : undefined}
            />
          )}
        </div>
      </div>

      {/* Table */}
      <UniversalList
        columns={columns}
        data={tasks}
        loading={loading}
        emptyMessage={t('tasks.no_tasks')}
        loadingText={t('common.loading')}
        rowKey="id"
        page={page}
        totalItems={total}
        pageSize={limit}
        setPage={setPage}
        setPageSize={setLimit}
      />

      {/* Batch Assign Modal */}
      {batchAssignIds && (
        <BatchAssignModal
          selectedIds={batchAssignIds}
          onClose={() => setBatchAssignIds(null)}
          onSuccess={() => { setBatchAssignIds(null); fetchTasks(); }}
        />
      )}
    </div>
  );
};

export default TaskList;
